import { useState, useRef, forwardRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import html2canvas from 'html2canvas';
import {
  Shield, ArrowRight, ArrowLeft, Check, X as XIcon, Award, Download, RefreshCw,
  Sparkles, ChevronRight, Loader2, Heart
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CERTIFICATE_TEMPLATE = "https://customer-assets.emergentagent.com/job_hygiene-landing-1/artifacts/vo3m0qno_35314F3F-F575-4071-B93E-2D4262133512.png";

const QUESTIONS = [
  "Do you always dispose of your waste in a dustbin instead of littering public places?",
  "If you cannot find a dustbin, do you keep your waste with you until you find one?",
  "Do you avoid spitting in public places?",
  "Do you leave public toilets clean and usable for the next person?",
  "Do you report dirty, damaged, or unhygienic public washrooms or surroundings to the concerned authority?",
  "Do you avoid writing on, damaging, or defacing public property such as walls, benches, buses, or parks?",
  "Do you ensure that food wrappers, bottles, tissues, and other waste are properly disposed of after picnics, travel, or public events?",
  "Do you politely encourage others to follow good public hygiene practices when appropriate?",
  "Do you avoid throwing waste from vehicles or while travelling?",
  "Do you reduce single-use plastics by carrying reusable bottles, bags, or containers whenever possible?",
  "Do you separate recyclable waste from general waste whenever facilities are available?",
  "Do you avoid wasting water in public places, such as public washrooms, parks, or drinking water stations?",
  "If you notice overflowing garbage bins or unhygienic public areas, do you report them instead of ignoring them?",
  "Do you believe that keeping shared spaces clean is everyone's responsibility, not just the government's or cleaning staff's?",
  "If you see litter on the ground and can safely pick it up, are you willing to do so?"
];

const PLEDGE = `I pledge to keep public places clean, use shared spaces responsibly, respect sanitation workers, protect the environment, and inspire others to build a cleaner, healthier, and more hygienic community. I will remember that every small action I take contributes to a better society.`;

// Stage: intro | info | questions | pledge | submitting | result
export const WarriorSection = () => {
  const [stage, setStage] = useState('intro');
  const [info, setInfo] = useState({
    name: '', email: '', phone: '', age: '', gender: '', city: '', state: '', occupation: ''
  });
  const [currentQ, setCurrentQ] = useState(0);
  const [answers, setAnswers] = useState(Array(15).fill(null));
  const [pledgeAccepted, setPledgeAccepted] = useState(false);
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const certRef = useRef(null);

  const handleStart = () => setStage('info');

  const handleInfoNext = (e) => {
    e.preventDefault();
    if (!info.name.trim() || !info.email.trim() || !info.phone.trim()) {
      alert('Please fill in your name, email and phone to continue.');
      return;
    }
    setStage('questions');
    setCurrentQ(0);
  };

  const handleAnswer = (yesOrNo) => {
    const next = [...answers];
    next[currentQ] = yesOrNo;
    setAnswers(next);

    if (currentQ < QUESTIONS.length - 1) {
      setTimeout(() => setCurrentQ(currentQ + 1), 350);
    } else {
      setTimeout(() => setStage('pledge'), 350);
    }
  };

  const handleBack = () => {
    if (currentQ > 0) setCurrentQ(currentQ - 1);
    else setStage('info');
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setSubmitError('');
    try {
      const payload = {
        name: info.name,
        email: info.email,
        phone: info.phone,
        age: info.age ? parseInt(info.age) : null,
        gender: info.gender || null,
        city: info.city || null,
        state: info.state || null,
        occupation: info.occupation || null,
        answers,
        pledge_accepted: pledgeAccepted,
      };
      const { data } = await axios.post(`${API_URL}/warrior/submit`, payload);
      setResult(data);
      setStage('result');
    } catch (err) {
      setSubmitError(err.response?.data?.detail || 'Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRestart = () => {
    setInfo({ name: '', email: '', phone: '', age: '', gender: '', city: '', state: '', occupation: '' });
    setAnswers(Array(15).fill(null));
    setCurrentQ(0);
    setPledgeAccepted(false);
    setResult(null);
    setStage('intro');
  };

  const downloadCertificate = async () => {
    if (!certRef.current) return;
    try {
      const canvas = await html2canvas(certRef.current, {
        useCORS: true,
        allowTaint: false,
        backgroundColor: '#ffffff',
        scale: 2,
      });
      const link = document.createElement('a');
      link.download = `Hygiene-Warrior-Certificate-${result.name.replace(/\s+/g, '-')}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch (e) {
      alert('Could not generate the certificate image — please try a screenshot instead.');
    }
  };

  return (
    <section
      id="warrior"
      className="min-h-screen py-16 md:py-24 px-4 md:px-8 relative overflow-hidden"
      data-testid="warrior-section"
    >
      {/* Animated colourful blobs background */}
      <FloatingBlobs />

      <div className="relative z-10 max-w-4xl mx-auto">
        <AnimatePresence mode="wait">
          {stage === 'intro' && <IntroStage key="intro" onStart={handleStart} />}
          {stage === 'info' && <InfoStage key="info" info={info} setInfo={setInfo} onNext={handleInfoNext} />}
          {stage === 'questions' && (
            <QuestionStage
              key={`q-${currentQ}`}
              questionIndex={currentQ}
              question={QUESTIONS[currentQ]}
              currentAnswer={answers[currentQ]}
              total={QUESTIONS.length}
              onAnswer={handleAnswer}
              onBack={handleBack}
            />
          )}
          {stage === 'pledge' && (
            <PledgeStage
              key="pledge"
              accepted={pledgeAccepted}
              setAccepted={setPledgeAccepted}
              onSubmit={handleSubmit}
              onBack={() => { setStage('questions'); setCurrentQ(QUESTIONS.length - 1); }}
              submitting={submitting}
              error={submitError}
            />
          )}
          {stage === 'result' && result && (
            <ResultStage
              key="result"
              result={result}
              certRef={certRef}
              onDownload={downloadCertificate}
              onRestart={handleRestart}
            />
          )}
        </AnimatePresence>
      </div>
    </section>
  );
};

// =============== Floating Blobs Background ===============
const FloatingBlobs = () => (
  <>
    <motion.div
      className="absolute top-10 left-10 w-72 h-72 rounded-full bg-[#87CEEB] opacity-30 blur-3xl"
      animate={{ x: [0, 30, 0], y: [0, 40, 0] }}
      transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
    />
    <motion.div
      className="absolute bottom-10 right-10 w-96 h-96 rounded-full bg-[#90EE90] opacity-25 blur-3xl"
      animate={{ x: [0, -40, 0], y: [0, -30, 0] }}
      transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut' }}
    />
    <motion.div
      className="absolute top-1/2 left-1/2 w-80 h-80 rounded-full bg-[#F4A261] opacity-20 blur-3xl"
      animate={{ x: [-100, 100, -100], y: [-50, 50, -50] }}
      transition={{ duration: 12, repeat: Infinity, ease: 'easeInOut' }}
    />
  </>
);

// =============== Stage Components ===============

const fadeUp = {
  initial: { y: 30, opacity: 0 },
  animate: { y: 0, opacity: 1 },
  exit: { y: -30, opacity: 0 },
  transition: { duration: 0.5, ease: [0.16, 1, 0.3, 1] }
};

const IntroStage = ({ onStart }) => (
  <motion.div {...fadeUp} className="text-center" data-testid="warrior-intro">
    <motion.div
      initial={{ scale: 0.5, rotate: -10 }}
      animate={{ scale: 1, rotate: 0 }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
      className="inline-flex items-center justify-center w-24 h-24 md:w-32 md:h-32 rounded-3xl bg-gradient-to-br from-[#87CEEB] to-[#90EE90] mb-6 shadow-2xl"
    >
      <Shield className="w-12 h-12 md:w-16 md:h-16 text-white" />
    </motion.div>

    <motion.p
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}
      className="text-white/90 text-xs md:text-sm tracking-[0.3em] uppercase mb-4 drop-shadow"
    >
      Take the Pledge
    </motion.p>

    <motion.h2
      initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.4 }}
      className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-medium text-white drop-shadow-2xl leading-tight"
    >
      Be a <span className="text-[#87CEEB]">Hygiene</span><br />
      <span className="font-serif italic text-[#90EE90]">Warrior</span>
    </motion.h2>

    <motion.p
      initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.6 }}
      className="mt-8 text-white/90 text-sm md:text-base max-w-xl mx-auto drop-shadow leading-relaxed"
    >
      Take the 15-question Hygiene Warrior Assessment. Score 10 or more, sign the pledge, and earn your official Hygiene Warrior Certificate.
    </motion.p>

    <motion.div
      initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.8 }}
      className="mt-10 flex flex-wrap items-center justify-center gap-6 text-white/80 text-xs"
    >
      <span className="flex items-center gap-2"><Sparkles className="w-4 h-4 text-[#90EE90]" /> 15 questions</span>
      <span className="flex items-center gap-2"><Award className="w-4 h-4 text-[#F4A261]" /> Certificate on passing</span>
      <span className="flex items-center gap-2"><Heart className="w-4 h-4 text-[#FF6B9D]" /> 5 mins</span>
    </motion.div>

    <motion.button
      initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 1.0 }}
      whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}
      onClick={onStart}
      className="mt-10 inline-flex items-center gap-3 px-10 py-4 bg-gradient-to-r from-[#87CEEB] to-[#90EE90] text-white rounded-full font-semibold text-base shadow-2xl hover:shadow-3xl"
      data-testid="warrior-start-btn"
    >
      Start the Assessment <ChevronRight className="w-5 h-5" />
    </motion.button>
  </motion.div>
);

const InfoStage = ({ info, setInfo, onNext }) => (
  <motion.form {...fadeUp} onSubmit={onNext} className="bg-white/95 backdrop-blur-xl rounded-3xl p-6 md:p-10 shadow-2xl" data-testid="warrior-info">
    <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">A few quick details</h3>
    <p className="text-gray-500 text-sm mb-6">These help us issue your personalised certificate. We respect your privacy.</p>

    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <Field label="Full Name *" value={info.name} onChange={(v) => setInfo({ ...info, name: v })} testId="info-name" required />
      <Field label="Email *" type="email" value={info.email} onChange={(v) => setInfo({ ...info, email: v })} testId="info-email" required />
      <Field label="Phone (WhatsApp) *" type="tel" value={info.phone} onChange={(v) => setInfo({ ...info, phone: v })} testId="info-phone" required />
      <Field label="Age" type="number" value={info.age} onChange={(v) => setInfo({ ...info, age: v })} testId="info-age" />

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Gender</label>
        <select
          value={info.gender}
          onChange={(e) => setInfo({ ...info, gender: e.target.value })}
          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#87CEEB] outline-none bg-white"
          data-testid="info-gender"
        >
          <option value="">Select…</option>
          <option value="Male">Male</option>
          <option value="Female">Female</option>
          <option value="Other">Other</option>
          <option value="Prefer not to say">Prefer not to say</option>
        </select>
      </div>

      <Field label="Occupation" value={info.occupation} onChange={(v) => setInfo({ ...info, occupation: v })} testId="info-occupation" placeholder="Student, Teacher, …" />
      <Field label="City" value={info.city} onChange={(v) => setInfo({ ...info, city: v })} testId="info-city" />
      <Field label="State" value={info.state} onChange={(v) => setInfo({ ...info, state: v })} testId="info-state" />
    </div>

    <button
      type="submit"
      className="mt-6 w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-gradient-to-r from-[#87CEEB] to-[#90EE90] text-white rounded-full font-semibold shadow-lg hover:shadow-xl transition-shadow"
      data-testid="info-next-btn"
    >
      Begin Assessment <ArrowRight className="w-4 h-4" />
    </button>
  </motion.form>
);

const Field = ({ label, value, onChange, type = 'text', testId, required = false, placeholder = '' }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-2">{label}</label>
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      required={required}
      placeholder={placeholder}
      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#87CEEB] outline-none"
      data-testid={testId}
    />
  </div>
);

const QuestionStage = ({ questionIndex, question, currentAnswer, total, onAnswer, onBack }) => {
  const progress = ((questionIndex + 1) / total) * 100;

  return (
    <motion.div {...fadeUp} className="bg-white/95 backdrop-blur-xl rounded-3xl p-6 md:p-10 shadow-2xl" data-testid={`warrior-q-${questionIndex}`}>
      {/* Progress bar */}
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-semibold text-gray-500 tracking-widest uppercase">
          Question {questionIndex + 1} of {total}
        </span>
        <span className="text-xs font-semibold text-[#87CEEB]">{Math.round(progress)}%</span>
      </div>
      <div className="h-2 bg-gray-100 rounded-full overflow-hidden mb-8">
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${progress}%` }}
          transition={{ duration: 0.5, ease: 'easeOut' }}
          className="h-full bg-gradient-to-r from-[#87CEEB] to-[#90EE90] rounded-full"
        />
      </div>

      <motion.h3
        initial={{ y: 15, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.4 }}
        className="text-xl md:text-2xl lg:text-3xl font-semibold text-gray-900 leading-snug min-h-[5rem]"
      >
        {question}
      </motion.h3>

      <div className="grid grid-cols-2 gap-4 mt-8">
        <motion.button
          whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}
          onClick={() => onAnswer(true)}
          className={`group relative overflow-hidden py-6 md:py-8 rounded-2xl font-bold text-lg md:text-xl border-2 transition-all ${
            currentAnswer === true
              ? 'bg-gradient-to-br from-[#90EE90] to-[#5CB85C] text-white border-[#5CB85C] shadow-2xl'
              : 'bg-white border-gray-200 text-gray-800 hover:border-[#90EE90] hover:bg-[#90EE90]/5'
          }`}
          data-testid={`answer-yes-${questionIndex}`}
        >
          <Check className="w-7 h-7 mx-auto mb-2" />
          YES
        </motion.button>

        <motion.button
          whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}
          onClick={() => onAnswer(false)}
          className={`group relative overflow-hidden py-6 md:py-8 rounded-2xl font-bold text-lg md:text-xl border-2 transition-all ${
            currentAnswer === false
              ? 'bg-gradient-to-br from-[#FF8A80] to-[#E53935] text-white border-[#E53935] shadow-2xl'
              : 'bg-white border-gray-200 text-gray-800 hover:border-[#FF8A80] hover:bg-[#FF8A80]/5'
          }`}
          data-testid={`answer-no-${questionIndex}`}
        >
          <XIcon className="w-7 h-7 mx-auto mb-2" />
          NO
        </motion.button>
      </div>

      <button
        onClick={onBack}
        className="mt-6 inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 transition-colors"
        data-testid={`back-btn-${questionIndex}`}
      >
        <ArrowLeft className="w-4 h-4" /> Back
      </button>
    </motion.div>
  );
};

const PledgeStage = ({ accepted, setAccepted, onSubmit, onBack, submitting, error }) => (
  <motion.div {...fadeUp} className="bg-white/95 backdrop-blur-xl rounded-3xl p-6 md:p-10 shadow-2xl" data-testid="warrior-pledge">
    <div className="text-center mb-6">
      <motion.div
        initial={{ scale: 0.5 }} animate={{ scale: 1 }}
        transition={{ type: 'spring', stiffness: 200 }}
        className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-[#F4A261] to-[#E76F51] mb-4 shadow-lg"
      >
        <Award className="w-8 h-8 text-white" />
      </motion.div>
      <h3 className="text-2xl md:text-3xl font-bold text-gray-900">The Hygiene Warrior Pledge</h3>
    </div>

    <blockquote className="relative font-serif italic text-base md:text-lg text-gray-700 text-center leading-relaxed bg-gradient-to-br from-[#F4F4F2] to-white border border-gray-200 rounded-2xl p-6 md:p-8">
      <span className="text-[#87CEEB] text-4xl absolute -top-2 left-3 font-serif">"</span>
      {PLEDGE}
      <span className="text-[#87CEEB] text-4xl absolute -bottom-6 right-3 font-serif">"</span>
    </blockquote>

    <label className="mt-6 flex items-start gap-3 cursor-pointer p-4 rounded-xl hover:bg-gray-50 transition-colors" data-testid="pledge-checkbox-label">
      <input
        type="checkbox"
        checked={accepted}
        onChange={(e) => setAccepted(e.target.checked)}
        className="mt-1 w-5 h-5 accent-[#87CEEB]"
        data-testid="pledge-checkbox"
      />
      <span className="text-sm md:text-base text-gray-800 font-medium">
        I accept this pledge and commit to being a Hygiene Warrior.
      </span>
    </label>

    {error && <p className="text-red-500 text-sm mt-3" data-testid="submit-error">{error}</p>}

    <div className="mt-6 flex flex-col sm:flex-row gap-3">
      <button
        onClick={onBack}
        className="flex-1 inline-flex items-center justify-center gap-2 px-6 py-3 bg-gray-100 text-gray-700 rounded-full font-medium hover:bg-gray-200"
        data-testid="pledge-back-btn"
      >
        <ArrowLeft className="w-4 h-4" /> Back
      </button>
      <button
        onClick={onSubmit}
        disabled={!accepted || submitting}
        className="flex-1 inline-flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-[#87CEEB] to-[#90EE90] text-white rounded-full font-semibold shadow-lg disabled:opacity-40 disabled:cursor-not-allowed"
        data-testid="pledge-submit-btn"
      >
        {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
        Submit My Assessment
      </button>
    </div>
  </motion.div>
);

const ResultStage = ({ result, certRef, onDownload, onRestart }) => {
  const passed = result.passed;
  return (
    <motion.div {...fadeUp} className="space-y-6" data-testid="warrior-result">
      {/* Score Banner */}
      <div className={`rounded-3xl p-8 text-center text-white shadow-2xl ${
        passed
          ? 'bg-gradient-to-br from-[#90EE90] via-[#5CB85C] to-[#2E7D32]'
          : 'bg-gradient-to-br from-[#FFB74D] via-[#F4A261] to-[#E76F51]'
      }`}>
        <motion.div
          initial={{ scale: 0 }} animate={{ scale: 1 }}
          transition={{ type: 'spring', stiffness: 200, delay: 0.2 }}
          className="inline-flex items-center justify-center w-20 h-20 bg-white/20 rounded-full mb-4 backdrop-blur"
        >
          {passed ? <Award className="w-10 h-10" /> : <Heart className="w-10 h-10" />}
        </motion.div>
        <h3 className="text-3xl md:text-4xl font-bold mb-2">
          {passed ? 'Congratulations!' : 'Thank you for participating!'}
        </h3>
        <p className="text-white/95 text-lg md:text-xl mb-4">
          You scored <span className="font-bold text-2xl">{result.score} / 15</span>
        </p>
        <p className="text-white/90 text-sm max-w-md mx-auto">
          {passed
            ? `You are officially a certified Hygiene Warrior! Download your certificate below.`
            : `You're on the right path. Keep learning, keep growing — small daily actions create a healthier world.`
          }
        </p>
      </div>

      {/* Certificate (only if passed) */}
      {passed && (
        <>
          <div className="bg-white/95 backdrop-blur-xl rounded-3xl p-4 md:p-6 shadow-2xl">
            <p className="text-center text-xs font-semibold text-gray-500 tracking-widest uppercase mb-3">
              Your Official Certificate · ID: {result.certificate_id}
            </p>
            <Certificate result={result} ref={certRef} />
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={onDownload}
              className="flex-1 inline-flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-[#87CEEB] to-[#90EE90] text-white rounded-full font-semibold shadow-lg"
              data-testid="download-cert-btn"
            >
              <Download className="w-4 h-4" /> Download Certificate
            </button>
            <button
              onClick={onRestart}
              className="flex-1 inline-flex items-center justify-center gap-2 px-6 py-3 bg-white border-2 border-gray-200 text-gray-700 rounded-full font-semibold hover:bg-gray-50"
              data-testid="restart-btn"
            >
              <RefreshCw className="w-4 h-4" /> Take Again
            </button>
          </div>
        </>
      )}

      {!passed && (
        <div className="flex gap-3 justify-center">
          <button
            onClick={onRestart}
            className="inline-flex items-center gap-2 px-8 py-3 bg-white border-2 border-gray-200 text-gray-700 rounded-full font-semibold hover:bg-gray-50"
            data-testid="restart-btn"
          >
            <RefreshCw className="w-4 h-4" /> Try Again
          </button>
        </div>
      )}
    </motion.div>
  );
};

// =============== Certificate (using user-provided template) ===============

const Certificate = forwardRef(({ result }, ref) => {
  const today = new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  return (
    <div
      ref={ref}
      className="relative w-full mx-auto bg-white"
      style={{ aspectRatio: '1492 / 1054', maxWidth: 900 }}
      data-testid="certificate"
    >
      <img
        src={CERTIFICATE_TEMPLATE}
        alt="Certificate"
        crossOrigin="anonymous"
        className="absolute inset-0 w-full h-full object-contain rounded-xl"
      />
      {/* Participant name overlay - inside the white box, centered between "PARTICIPANT NAME" label and blue line */}
      <div
        className="absolute flex items-center justify-center"
        style={{ left: '37%', top: '84%', width: '26%', height: '5%' }}
      >
        <p
          className="font-serif text-center text-[#15326b] font-bold whitespace-nowrap"
          style={{
            fontSize: 'clamp(11px, 2.2vw, 20px)',
            letterSpacing: '0.06em',
            lineHeight: 1
          }}
          data-testid="certificate-name"
        >
          {result.name.toUpperCase()}
        </p>
      </div>
      {/* Date overlay - just above the "DATE" label */}
      <div
        className="absolute flex items-end justify-center"
        style={{ left: '77%', top: '86%', width: '12%', height: '4%' }}
      >
        <p
          className="font-serif text-[#15326b] font-semibold whitespace-nowrap"
          style={{ fontSize: 'clamp(8px, 1.1vw, 12px)', lineHeight: 1 }}
          data-testid="certificate-date"
        >
          {today}
        </p>
      </div>
    </div>
  );
});
Certificate.displayName = 'Certificate';
